import React, { useCallback } from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { Button } from '../button/Button';
import * as DateUtils from './dateUtils';
import DayPicker from './DayPicker';
import { TimePicker } from './TimePicker';
import * as TimeUnit from './timeUnit';

const { Modifiers } = DateUtils;

const DayPickerWrapper = styled.div`
  user-select: none;
`;

const DayPickerFooter = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px;
`;

const TimePickerWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
`;

const Divider = styled.div`
  border-bottom: ${themeGet('calendar.separator.border')};
`;

export const DateTimePicker = ({
  value,
  onChange,
  month,
  maxDate,
  minDate,
  canClearSelection,
  showActionsBar,
  showTimePicker,
  precision,
  required,
  disabled,
  isMobile,
}) => {
  const initialDate = DateUtils.getInitialDate({ maxDate, minDate }, value);
  const displayMonth = initialDate.getMonth();
  const displayYear = initialDate.getFullYear();
  const initialMonth = new Date(displayYear, displayMonth);

  const handleDayClick = useCallback(
    (day, modifiers, e) => {
      e.preventDefault();
      if (modifiers.disabled) {
        return;
      }

      const newValue =
        canClearSelection && modifiers.selected ? null : DateUtils.getDateTime(day, initialDate);

      onChange({
        ...e,
        target: {
          ...e.target,
          value: newValue,
        },
      });
    },
    [canClearSelection, initialDate, onChange]
  );

  const handleTodayClick = useCallback(
    e => {
      e.preventDefault();
      onChange({
        ...e,
        target: {
          ...e.target,
          value: new Date(),
        },
      });
    },
    [onChange]
  );

  const handleClear = useCallback(
    e => {
      e.preventDefault();
      onChange({
        ...e,
        target: {
          ...e.target,
          value: null,
        },
      });
    },
    [onChange]
  );

  const modifiers = {
    [Modifiers.DISABLED]: day => !DateUtils.isDayInRange(day, [minDate, maxDate]),
  };

  const disableTodayButton = !DateUtils.isDayInRange(new Date(), [minDate, maxDate]);

  return (
    <DayPickerWrapper>
      <DayPicker
        onChange={onChange}
        fromMonth={minDate}
        toMonth={maxDate}
        month={month || initialMonth}
        onDayClick={handleDayClick}
        selectedDays={value || null}
        modifiers={modifiers}
        showOutsideDays
        canChangeMonth
      />
      {showTimePicker && (
        <TimePickerWrapper>
          <TimePicker date={initialDate} onChange={onChange} precision={precision} isMobile={isMobile} />
        </TimePickerWrapper>
      )}
      {showActionsBar && (
        <>
          <Divider />
          <DayPickerFooter>
            <Button
              variant="link"
              size="small"
              onClick={handleTodayClick}
              disabled={disableTodayButton || disabled}
            >
              Today
            </Button>
            <Button variant="link" size="small" onClick={handleClear} disabled={disabled || required}>
              Clear
            </Button>
          </DayPickerFooter>
        </>
      )}
    </DayPickerWrapper>
  );
};

DateTimePicker.defaultProps = {
  maxDate: DateUtils.getDefaultMaxDate(),
  minDate: DateUtils.getDefaultMinDate(),
  precision: TimeUnit.TimePrecision.MINUTE,
};
